import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { Auth0Provider, useUser } from '@auth0/nextjs-auth0/client'

import SpinnerOverlay from '~/components/shared/SpinnerOverlay'
import { PlatformError } from '~/utils/errors'
import { isClient } from '~/utils/envChecks'
import { StorageKeys, useStateStorage } from '@reward-platform/utils/client'

import {
  AuthPathKey,
  AuthPathOptions,
  createAuthPathname,
  getMembershipIdFromSub,
} from '@reward-platform/auth0-b2c/utils'
import { clearRecentSearchesFromLocalStorage } from '~/components/search-and-results/RecentSearches/RecentSearches.storage'
import { AviosAuthProvider, useOptionalAviosAuth } from '@channel/shared-header-footer/auth-client'
import { setClientRequestHeader, deleteClientRequestHeader } from '~/services/clients/nextApiClient'
import { usePlatformConfig } from '../platform'
import { AuthContextValue, AuthProviderProps } from './Auth.types'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)
const AviosAuthEnabledContext = createContext(false)

export const useAviosAuthEnabled = (): boolean => useContext(AviosAuthEnabledContext)

export const AviosAuthEnabledProvider = ({
  children,
  enabled,
}: React.PropsWithChildren<{ enabled: boolean }>) => (
  <AviosAuthEnabledContext.Provider value={enabled}>{children}</AviosAuthEnabledContext.Provider>
)

const handleClientAuthRoute = (type: AuthPathKey, options: AuthPathOptions) => {
  if (isClient()) {
    const pathname = `${createAuthPathname(type, { ...options })}`
    window.location.assign(pathname)
  }
}

const AuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const authUser = useUser()
  const { basePath } = usePlatformConfig()
  const aviosAuthEnabled = useAviosAuthEnabled()
  const { user } = authUser
  const userId: string | null = user?.sub ? getMembershipIdFromSub(user.sub) : null
  const isAuthenticated = !!userId

  const { removeStorage } = useStateStorage({
    key: StorageKeys.BASKET_TRAVELER_INFORMATION,
    parser: (value) => {
      return value ?? null
    },
  })
  const { removeStorage: removeAdditionalStorage } = useStateStorage({
    key: StorageKeys.BASKET_ADDITIONAL_DETAILS,
    parser: (value) => {
      return value ?? null
    },
  })

  useEffect(() => {
    sessionStorage.setItem('authenticated_user', isAuthenticated ? 'true' : 'false')
  }, [isAuthenticated])

  const prevIsAuthenticated = React.useRef(isAuthenticated)
  useEffect(() => {
    if (prevIsAuthenticated.current && !isAuthenticated) {
      // Session expired or silent login failed — user transitioned from authenticated to unauthenticated
      clearRecentSearchesFromLocalStorage()
    }
    prevIsAuthenticated.current = isAuthenticated
  }, [isAuthenticated, removeStorage, removeAdditionalStorage])

  const contextValue = useMemo<AuthContextValue>(() => {
    return {
      ...authUser,
      userId,
      isAuthenticated,
      handleLogin: (options?: AuthPathOptions) => {
        if (aviosAuthEnabled) {
          return
        }

        handleClientAuthRoute('LOGIN', { basePath, ...options })
      },
      handleLogout: (options?: AuthPathOptions) => {
        removeStorage()
        removeAdditionalStorage()
        clearRecentSearchesFromLocalStorage()
        handleClientAuthRoute('LOGOUT', { basePath, ...options })
      },
      getAuthHref: (type: AuthPathKey, options?: AuthPathOptions) => {
        if (aviosAuthEnabled && type === 'LOGIN') {
          return basePath || '/'
        }

        return createAuthPathname(type, { basePath, ...options })
      },
    }
  }, [
    authUser,
    userId,
    isAuthenticated,
    basePath,
    aviosAuthEnabled,
    removeStorage,
    removeAdditionalStorage,
  ])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

const AviosAuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const aviosAuth = useOptionalAviosAuth()
  const { basePath } = usePlatformConfig()

  const user = aviosAuth?.user
  const userId: string | null = user?.tokenContent?.membershipNumber ?? null
  const isAuthenticated =
    !!aviosAuth?.isAuthenticated || !!user?.accessToken || !!user?.tokenContent?.sub
  const isAviosLoading = aviosAuth ? aviosAuth.isLoading : false

  // Set the header before child component renders
  useEffect(() => {
    const accessToken = user?.accessToken
    if (accessToken) {
      setClientRequestHeader('authorization', `Bearer ${accessToken}`)
    } else {
      deleteClientRequestHeader('authorization')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.accessToken])
  const { removeStorage } = useStateStorage({
    key: StorageKeys.BASKET_TRAVELER_INFORMATION,
    parser: (value) => {
      return value ?? null
    },
  })
  const { removeStorage: removeAdditionalStorage } = useStateStorage({
    key: StorageKeys.BASKET_ADDITIONAL_DETAILS,
    parser: (value) => {
      return value ?? null
    },
  })

  useEffect(() => {
    sessionStorage.setItem('authenticated_user', isAuthenticated ? 'true' : 'false')
  }, [isAuthenticated])

  const prevIsAuthenticated = React.useRef(isAuthenticated)
  useEffect(() => {
    if (prevIsAuthenticated.current && !isAuthenticated) {
      // User transitioned from authenticated to unauthenticated (session expired, silent login
      // failed, or signed out via the shared header, which owns the logout action and does not
      // go through handleLogout). Clear everything handleLogout would clear.
      removeStorage()
      removeAdditionalStorage()
      clearRecentSearchesFromLocalStorage()
    }
    prevIsAuthenticated.current = isAuthenticated
  }, [isAuthenticated, removeStorage, removeAdditionalStorage])

  const aviosAuthRef = useRef(aviosAuth)
  aviosAuthRef.current = aviosAuth

  const contextValue = useMemo<AuthContextValue>(() => {
    return {
      user,
      userId,
      isAuthenticated,
      isLoading: isAviosLoading,
      error: undefined,
      invalidate: async () => undefined,
      handleLogin: (options?: AuthPathOptions) => {
        aviosAuthRef.current?.login({})
      },
      handleLogout: (options?: AuthPathOptions) => {
        removeStorage()
        removeAdditionalStorage()
        clearRecentSearchesFromLocalStorage()
        aviosAuthRef.current?.logout({})
      },
      getAuthHref: (type: AuthPathKey, options?: AuthPathOptions) => {
        if (type === 'LOGIN') {
          return basePath || '/'
        }

        return createAuthPathname(type, { basePath, ...options })
      },
    }
  }, [
    user,
    userId,
    isAuthenticated,
    isAviosLoading,
    basePath,
    removeStorage,
    removeAdditionalStorage,
  ])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
export const AuthProvider = ({
  isLoading,
  aviosAuthEnabled,
  initialUser,
  platformTheme,
  ...rest
}: React.PropsWithChildren<AuthProviderProps>) => {
  if (isLoading) {
    return <SpinnerOverlay />
  }

  if (!aviosAuthEnabled) {
    return (
      <Auth0Provider>
        <AuthContextProvider {...rest} />
      </Auth0Provider>
    )
  }

  return (
    <AviosAuthEnabledProvider enabled={aviosAuthEnabled}>
      <AviosAuthProvider initialUser={initialUser}>
        <AviosAuthContextProvider>{rest.children}</AviosAuthContextProvider>
      </AviosAuthProvider>
    </AviosAuthEnabledProvider>
  )
}

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new PlatformError('useAuthContext must be used within an AuthProvider')
  }
  return context ?? {}
}

export default AuthContext
